import { FileWarning } from 'lucide-react';
import type { ReactElement } from 'react';

import { InlineError } from '@/components/ui/StatePanel';
import { usePermission } from '@/hooks/usePermission';

import type { FlowAction, FlowCapabilities } from '../types';
import { trainingForVersion } from '../training-stage';
import { useFlowHistory } from '../use-flow-history';
import { useTrainingStage } from '../use-training-stage';
import { ChangeLogCard } from './ChangeLogCard';
import { CommentsThread } from './CommentsThread';
import { FlowStepper } from './FlowStepper';
import { StageActions } from './StageActions';
import { StageClockCard } from './StageClockCard';
import { StageResponsiblesCard } from './StageResponsiblesCard';
import { errorAdvice } from '@/lib/error-advice';

interface Props {
  caps: FlowCapabilities;
  companyId: string | null;
  stageActions: FlowAction[];
  onSelect: (a: FlowAction) => void;
  pending: boolean;
}

/** Columna lateral del editor: etapas, reloj, control de cambios, comentarios y las
 *  acciones de la etapa. Los modales de esas acciones los maneja EditorBody. */
export function EditorStack({
  caps,
  companyId,
  stageActions,
  onSelect,
  pending,
}: Props): ReactElement {
  // G2-4: mide su trabajo quien opera la etapa (responsable asignado o Calidad).
  const canTrack = caps.actingAs !== null;
  const canSeeStandard = usePermission('quality:time-standards:read');
  // G4: la capacitación es la cuarta etapa del stepper (estado derivado del backend),
  // solo la de la versión de este flujo (no la de la vigente durante una modificación).
  const training = useTrainingStage(caps.entryId, { companyId, enabled: true });
  const history = useFlowHistory(caps.flowInstanceId, { companyId, enabled: true });
  // UX-ERR-01: sin historial o sin la etapa de capacitación el stepper se vería incompleto.
  const stepperFailed = history.isError || training.isError;
  const retryStepper = (): void => {
    if (history.isError) {
      void history.refetch();
    }
    if (training.isError) {
      void training.refetch();
    }
  };
  return (
    <div className="stack">
      <FlowStepper
        currentStatus={caps.currentStatus}
        history={history.data ?? []}
        training={trainingForVersion(training.data ?? null, caps.targetVersion)}
      />
      {stepperFailed && (
        <div className="card">
          <InlineError
            icon={<FileWarning />}
            onRetry={retryStepper}
            retrying={history.isFetching || training.isFetching}
          >
            No se pudo cargar el avance del flujo: las etapas pueden verse incompletas.{' '}
            {errorAdvice(history.error ?? training.error)}
          </InlineError>
        </div>
      )}
      {caps.stage !== null ? (
        <StageResponsiblesCard stage={caps.stage} actingAs={caps.actingAs} />
      ) : null}
      {caps.flowInstanceId !== null ? (
        <StageClockCard
          flowInstanceId={caps.flowInstanceId}
          companyId={companyId}
          canTrack={canTrack}
          canSeeStandard={canSeeStandard}
        />
      ) : null}
      <ChangeLogCard caps={caps} companyId={companyId} />
      <CommentsThread
        entryId={caps.entryId}
        companyId={companyId}
        canComment={caps.documentCapabilities.comment}
      />
      <StageActions
        actions={stageActions}
        onSelect={onSelect}
        pending={pending}
        context={{
          role: caps.stage?.role,
          pending: caps.stage?.slots.filter((s) => s.status === 'pending').map((s) => s.label),
        }}
      />
    </div>
  );
}
